import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IFuelReading } from '../interfaces/fuel-reading';
import { ILateStatuses } from '../interfaces/late-statuses';
import { IOdometerReading } from '../interfaces/odometer-reading';
import { IPageResult } from '../interfaces/page-result';
import { ITaskFilter } from '../interfaces/task-filter';
import {
  IAddPhotoTaskStepResult,
  IDeleteChecklistPhoto,
  IDriverMessage,
  IOrderInfo,
  IPhotoTaskStep,
  ITaskInfo,
  ITaskNote,
  TaskSearchRequestDTO,
  TaskSearchResultDTO,
  ITaskStep,
  ITaskStepPhotoInfo,
  ITaskLocationUpdateContext,
  ITaskWindowUpdate,
} from '../interfaces/task-info';
import { ITaskType } from '../interfaces/task-type';
import { ReadService } from './read.service';
import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';
import FileSaver from 'file-saver';
import { MessageService } from 'primeng';
import { Message } from '../classes/Message';
import { IPauseTask } from '../interfaces/pause-task';
import { ITaskContactDetails } from '../interfaces/task-contact-details';
import moment from 'moment';
import { UpdateTaskScheduleDTO } from '../components/task-supplier-schedule/task-supplier-schedule';
import { MoDelPinDTO } from '../interfaces/moDel-pin-dto';
import { ReleaseCodeDTO } from '../interfaces/release-code-dto';
import { TaskPriceResult } from '../interfaces/customer-price-result/customer-price-result';
import { ISaveRegStatus } from '../interfaces/save-reg-status';
import { JobScreenTaskDetails } from '../interfaces/screen-specific/job-screen';
import { JobScreenVehicleCleanlinessTask } from '../interfaces/screen-specific/job-screen/common/job-screen-vehicle-cleanliness';
import { IFuelLevelMapper } from '../interfaces/fuel-level-mapper';

@Injectable({ providedIn: 'root' })
export class TaskService extends ReadService<ITaskInfo, ITaskFilter> {
  url = '/api/task';
  entityTitle = 'Task';

  constructor(
    http: HttpClient,
    private messageService: MessageService,
    handlersFactory: HttpResponsesHandlersFactory,
  ) {
    super(http, handlersFactory);
  }

  public GetTask(taskId: string): Promise<ITaskInfo> {
    return this.http.get<ITaskInfo>('api/task/taskInfo?taskId=' + taskId).toPromise();
  }

  public GetTaskTypes(): Promise<Array<ITaskType>> {
    return this.http.get<ITaskType[]>('api/task/tasktypes').toPromise();
  }

  public SaveNewOrders(newOrders: Array<IOrderInfo>): Promise<Array<IOrderInfo>> {
    return this.http.post<IOrderInfo[]>('api/order/saveOrders', { orders: newOrders }).toPromise();
  }

  public SaveContactDetails(taskContactDetails: ITaskContactDetails): Promise<ITaskContactDetails> {
    return this.http
      .post<ITaskContactDetails>('api/task/saveContactDetails', taskContactDetails)
      .toPromise();
  }

  public UpdateOrgStepTaskAnswers(taskId, orgStepId, updateDTO) {
    return this.http
      .post<any>(`api/order/task/${taskId}/orgStep/${orgStepId}/update`, updateDTO)
      .toPromise();
  }

  public saveNewLocations(
    task: ITaskInfo,
    updateDropoffWindows: boolean,
    isDryRun: boolean,
  ): Promise<TaskPriceResult> {
    const params = new HttpParams({ fromObject: { isDryRun, updateDropoffWindows } });
    return this.http
      .put<TaskPriceResult>(`api/task/${task.id}/locations`, task, { params })
      .toPromise();
  }

  public setCarryOver(id: number, carryOverAuthorised: boolean, comment: string) {
    return this.http
      .post<any>(`api/task/${id}/carryOver/${carryOverAuthorised}`, { comment: comment })
      .toPromise();
  }

  public search(dto: TaskSearchRequestDTO): Promise<TaskSearchResultDTO> {
    const myObject = JSON.parse(JSON.stringify(dto));
    const params = new HttpParams({ fromObject: myObject });

    return this.http.get<TaskSearchResultDTO>('api/task/search', { params: params }).toPromise();
  }

  public GetTaskGuranteeFulfilment(task: ITaskInfo): Promise<boolean> {
    return this.http.post<boolean>('api/task/guranteefulfilment', task).toPromise();
  }

  public GetLiveTasks(filter: any): Promise<IPageResult<ITaskInfo>> {
    let params = this.constructHttpParams(filter);
    return this.http
      .get<IPageResult<ITaskInfo>>('api/task/livetasks', { params: params })
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  public downloadLiveTasksToCSV(filter: ITaskFilter) {
    let params = this.constructHttpParams(filter);

    let filename = 'LiveTasks_' + moment(Date()).format('YYYYMMMMDo_h:mm') + '.csv';

    let report = this.http.get(`${this.url}/downloadLiveTasksToCSV`, {
      params: params,
      responseType: 'blob',
    });
    report.subscribe(
      (x) => this.handleDownloadFile(x, filename),
      (error) => {
        this.messageService.add(Message.Error('Impossible to download the csv.'));
      },
      () => {
        this.messageService.add(
          Message.Success('Downloaded successful. Check your download folder.'),
        );
      },
    );

    return report;
  }

  public handleDownloadFile(blob, filename) {
    FileSaver.saveAs(blob, filename);
  }

  public GetOfflineTasks(filter: any): Promise<IPageResult<ITaskInfo>> {
    let params = this.constructHttpParams(filter);
    return this.http
      .get<IPageResult<ITaskInfo>>('api/task/offlinetasks', { params: params })
      .toPromise()
      .then(this.extractData)
      .catch(this.handleError);
  }

  getDriverMessages(taskId: number): Promise<IDriverMessage[]> {
    return this.http.get<IDriverMessage[]>('api/task/getDriverMessages/' + taskId).toPromise();
  }

  getNotes(taskId: number): Promise<ITaskNote[]> {
    return this.http.get<ITaskNote[]>('api/task/getNotes/' + taskId).toPromise();
  }

  public AddPhotoToTaskStep(photoInfo: any): Promise<IPhotoTaskStep> {
    return this.http.post<IPhotoTaskStep>(this.url + '/addphotototaskstep', photoInfo).toPromise();
  }

  public AddMultiplePhotosToTaskStep(
    taskStepPhotoInfo: ITaskStepPhotoInfo[],
  ): Promise<IAddPhotoTaskStepResult[]> {
    return this.http
      .post<IAddPhotoTaskStepResult[]>(this.url + '/addmultiplephotostotaskstep', taskStepPhotoInfo)
      .toPromise();
  }

  public completeTaskStep(data) {
    return this.http.post(this.url + '/completetaskstep', data).toPromise();
  }

  public getCompletedTaskSteps(taskId: number) {
    return this.http.get<ITaskStep[]>('api/task/completedTaskSteps/' + taskId).toPromise();
  }

  setAreaCondition(data: any) {
    return this.http.post<boolean>('api/task/setareacondition', data).toPromise();
  }

  public deletePhotoToTaskStep(photoInfo: any): Promise<boolean> {
    return this.http.post<boolean>(this.url + '/deletephotototaskstep', photoInfo).toPromise();
  }

  public updatePhotoToTaskStep(photoInfo: any): Promise<boolean> {
    return this.http.post<boolean>(this.url + '/updatephotototaskstep', photoInfo).toPromise();
  }

  public checkIfCanChangeVehicle(task) {
    return this.http.post(this.url + '/checkifcanchangevehicle', task).toPromise();
  }

  public checkIfTaskExists(task): Promise<any> {
    console.log('Task to update: ', task);

    return this.http.post(this.url + '/checkiftaskexists', task).toPromise();
  }

  public saveActualTaskType(task): Promise<any> {
    return this.http.post(this.url + '/saveactualtasktype', task).toPromise();
  }

  public getActualTaskTypes(): Promise<any> {
    return this.http.get<ITaskType[]>('api/task/actualtasktypes').toPromise();
  }

  public saveLateStatuses(data: ILateStatuses) {
    return this.http.post<boolean>('api/task/savelatestatuses', data).toPromise();
  }

  public saveOdometerReading(data: IOdometerReading) {
    return this.http.post<boolean>('api/task/saveodometerreading', data).toPromise();
  }

  public updateMoDelPin(data: MoDelPinDTO) {
    return this.http.patch<boolean>('api/task/update-model-pin', data).toPromise();
  }

  public updateReleaseCode(data: ReleaseCodeDTO) {
    return this.http.patch<boolean>('api/task/update-release-code', data).toPromise();
  }

  public saveFuelReading(data: IFuelReading) {
    return this.http.post<boolean>('api/task/savefuelreading', data).toPromise();
  }

  public saveRegStatus(request: ISaveRegStatus): Promise<any> {
    return this.http.post(this.url + '/saveregstatus', request).toPromise();
  }

  public getRegStatuses(): Promise<any[]> {
    return this.http.get<any[]>('api/task/regstatuses').toPromise();
  }

  public getStepTypes(): Promise<any[]> {
    return this.http.get<any[]>('api/task/dynamicsteptypes').toPromise();
  }

  public pauseTask(jobId: number, taskId: number, data: IPauseTask) {
    return this.http.post<boolean>(`api/task/pauseTask/${jobId}/${taskId}`, data).toPromise();
  }

  public resumeTask(jobId: number, taskId: number, data: IPauseTask) {
    return this.http.post<boolean>(`api/task/resumeTask/${jobId}/${taskId}`, data).toPromise();
  }

  public deleteChecklistPhoto(photoInfo: IDeleteChecklistPhoto): Promise<boolean> {
    return this.http.post<boolean>(this.url + '/deleteChecklistPhoto', photoInfo).toPromise();
  }

  public saveSupplierSchedule(schedule: UpdateTaskScheduleDTO): Promise<any> {
    return this.http.post('/api/Task/SupplierSchedule', schedule).toPromise();
  }

  saveDriverNote(param: {
    id: number;
    driverId: number;
    note: string;
    noteDateTo: string;
    noteDateFrom: string;
  }) {
    return this.http.put('/api/Task/driverNote', param).toPromise();
  }

  printTasks(param: { tasks: string }) {
    return this.http.get('/api/Task/printTasks', { params: param }).toPromise();
  }

  checkForDuplicateMovements(dto: { registrationNumber: string; taskType: number }) {
    return this.http.post(`${this.url}/checkForDuplicateMovements`, dto).toPromise();
  }

  getTaskLocationUpdateContext(taskId: number): Promise<ITaskLocationUpdateContext> {
    return this.http
      .get<ITaskLocationUpdateContext>(`${this.url}/${taskId}/location-update-context`)
      .toPromise();
  }

  public getForComponent<T>(id: number | string, component: string): Promise<T> {
    return this.http.get<T>(`/api/task/${id}/component/${component}`).toPromise();
  }

  updateTaskWindows(request: ITaskWindowUpdate): Promise<void> {
    return this.http.patch<void>(`api/task/${request.id}/windows`, request).toPromise();
  }

  updateDriverNote(taskId: number, id: number, description: string): Promise<void> {
    return this.http.patch<void>(`api/task/${taskId}/note/${id}`, { id, description }).toPromise();
  }

  deleteDriverNote(taskId: number, noteId: number): Promise<void> {
    return this.http.delete<void>(`api/task/${taskId}/note/${noteId}`).toPromise();
  }

  async getChecklists(taskIds: number[]) {
    return this.http.post<{ [key: number]: any }>(`${this.url}/checklists`, taskIds).toPromise();
  }

  getJobScreenTaskDetails(taskIds: number[]) {
    const params = this.constructHttpParams({ taskIds });

    return this.http
      .get<JobScreenTaskDetails[]>(`${this.url}/getJobScreenTaskDetails`, { params })
      .toPromise();
  }

  saveVehicleCleanliness(data: JobScreenVehicleCleanlinessTask) {
    return this.http.post<boolean>('api/task/saveVehicleCleanliness', data).toPromise();
  }
}
